import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Artist } from '../../../components/roster/types';
import { artistsApi } from '../../../repositories/artists';
import ArtistDetailsTab from '../../../components/roster/ArtistDetailsTab';
import ArtistPitchesTab from '../../../components/roster/ArtistPitchesTab';
import AssetsTab from '../../../components/roster/AssetsTab';
import Swal from 'sweetalert2';


const ArtistDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [artist, setArtist] = useState<Artist | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  // const initialTab = (searchParams.get('tab') as 'details' | 'pitches' | 'assets') || 'details';
  const [activeTab, setActiveTab] = useState<'details' | 'pitches' | 'assets'>('details');
  useEffect(() => {
    if (id) {
      loadArtist(id);
    }
  }, [id]);

  // Update activeTab when URL changes
  useEffect(() => {
    const tab = (searchParams.get('tab') as 'details' | 'pitches' | 'assets') || 'details';
    setActiveTab(tab);
  }, [searchParams]);

  const loadArtist = async (artistId: string) => {
    try {
      setLoading(true);
      const response = await artistsApi.getOne(artistId);
      const artistData = response.data.data || response.data;

      if (artistData) {
        // Map backend field names to frontend field names
        const mappedArtist: Artist = {
          id: artistData.id,
          userId: artistData.userId,
          artistName: artistData.artistName,
          photo: artistData.photo,
          spotifyURI: artistData.spotifyURI,
          appleID: artistData.appleID,
          asin: artistData.asin,
          soundcloudURL: artistData.soundcloudURL,
          tidalURL: artistData.tidalURL,
          bio: artistData.bio,
          hometown: artistData.hometown,
          currentTown: artistData.currentTown,
          country: artistData.country,
          label: artistData.label,
          distributor: artistData.distributor,
          instagram: artistData.instagram,
          tiktok: artistData.tiktok,
          twitter: artistData.twitter,
          facebook: artistData.facebook,
          snapchat: artistData.snapchat,
          spotifyURL: artistData.spotifyURL,
          appleURL: artistData.appleURL,
          amazonURL: artistData.amazonURL,
          deezerURL: artistData.deezerURL,
          createdAt: new Date(artistData.createdAt),
          updatedAt: new Date(artistData.updatedAt),
        };

        setArtist(mappedArtist);
      } else {
        Swal.fire({
          title: 'Artist Not Found',
          text: 'The artist you are looking for does not exist.',
          icon: 'error',
          confirmButtonText: 'OK'
        });
        navigate('/roster');
      }
    } catch (error: any) {
      console.error('Error loading artist:', error);

      const errorMessage = error.response?.status === 404
        ? 'The artist you are looking for does not exist.'
        : 'Failed to load artist details. Please try again later.';

      Swal.fire({
        title: 'Error',
        text: errorMessage,
        icon: 'error',
        confirmButtonText: 'OK'
      });
      navigate('/roster');
    } finally {
      setLoading(false);
    }
  };

  const handleArtistUpdate = (updatedArtist: Artist) => {
    setArtist(updatedArtist);
  };

  const handleTabChange = (tab: 'details' | 'pitches' | 'assets') => {
    setActiveTab(tab);
    setSearchParams({ tab });
  }
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="w-12 h-12 border-b-2 border-indigo-600 rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!artist) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900">Artist not found</h2>
          <button
            onClick={() => navigate('/roster')}
            className="mt-4 text-indigo-600 hover:text-indigo-500"
          >
            Return to Roster
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="px-4 py-8 mx-auto max-w-7xl sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/roster')}
            className="flex items-center mb-4 text-gray-600 hover:text-gray-800"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Roster
          </button>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow">
          <div className="border-b border-gray-200">
            <nav className="flex">
              <button
                onClick={() => handleTabChange('details')}
                className={`px-6 py-3 text-sm font-medium border-b-2 ${activeTab === 'details'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
              >
                Details
              </button>
              <button
                onClick={() => handleTabChange('pitches')}
                className={`px-6 py-3 text-sm font-medium border-b-2 ${activeTab === 'pitches'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
              >
                Pitches
              </button>
              <button
                onClick={() => handleTabChange('assets')}
                className={`px-6 py-3 text-sm font-medium border-b-2 ${activeTab === 'assets'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
              >
                Assets
              </button>
            </nav>
          </div>

          <div className="p-6">
            {activeTab === 'details' ? (
              <ArtistDetailsTab
                artist={artist}
                onUpdate={handleArtistUpdate}
              />
            ) : activeTab === 'pitches' ? (
              <ArtistPitchesTab artistId={artist.id} />
            ) : (
              <AssetsTab artistId={artist.id} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ArtistDetail;